import React from 'react';
import { PrioritizationAssessment } from '../../types/workflow.types';

interface PrioritySignalCardProps {
  /** The prioritization assessment result from the AI */
  result: PrioritizationAssessment | null;
  /** Whether the prioritization check is still loading */
  loading: boolean;
  /** Whether the user has dismissed the card */
  dismissed: boolean;
  /** Callback when user dismisses the card */
  onDismiss: () => void;
}

/**
 * PrioritySignalCard - Shows the AI priority assessment before submission.
 * 
 * Two states:
 * - ✅ Meets Acceptance Criteria (green)
 * - ⚠️ Does Not Meet Acceptance Criteria (amber/orange)
 * 
 * Always dismissible, never blocks submission.
 */
const PrioritySignalCard: React.FC<PrioritySignalCardProps> = ({
  result,
  loading,
  dismissed,
  onDismiss
}) => {
  // Don't render if dismissed or no result and not loading
  if (dismissed) return null;
  if (!loading && !result) return null;

  // Loading skeleton
  if (loading) {
    return (
      <div className="priority-signal-card priority-signal-loading" style={cardStyles.container}>
        <div style={cardStyles.header}>
          <span style={cardStyles.headerIcon}>📊</span>
          <span style={cardStyles.headerTitle}>Priority Assessment</span>
        </div>
        <div style={cardStyles.loadingContent}>
          <div style={cardStyles.shimmer}></div>
          <p style={cardStyles.loadingText}>Analyzing your request against acceptance criteria...</p>
        </div>
        <style>{shimmerAnimation}</style>
      </div>
    );
  }

  if (!result) return null;

  const meets = result.meetsAcceptanceCriteria;

  return (
    <div 
      className={`priority-signal-card ${meets ? 'priority-meets' : 'priority-does-not-meet'}`}
      style={{
        ...cardStyles.container,
        borderLeft: meets ? '4px solid #38a169' : '4px solid #dd6b20',
      }}
    >
      {/* Header with dismiss button */}
      <div style={cardStyles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={cardStyles.headerIcon}>📊</span>
          <span style={cardStyles.headerTitle}>Priority Assessment</span>
        </div>
        <button 
          onClick={onDismiss}
          style={cardStyles.dismissBtn}
          aria-label="Dismiss priority assessment"
          title="Dismiss"
        >
          ✕
        </button>
      </div>

      {/* Badge */}
      <div style={{
        ...cardStyles.badge,
        backgroundColor: meets ? '#f0fff4' : '#fffaf0',
        border: meets ? '1.5px solid #38a169' : '1.5px solid #dd6b20',
        color: meets ? '#276749' : '#7b341e',
      }}>
        <span style={{ fontSize: '16px' }}>{meets ? '✅' : '⚠️'}</span>
        <span style={{ fontWeight: 600, fontSize: '14px' }}>
          {meets ? 'Meets Acceptance Criteria' : 'Does Not Meet Acceptance Criteria'}
        </span>
      </div>

      {/* Rationale */}
      <p style={cardStyles.rationale}>
        {result.rationale}
      </p>

      {/* Disclaimer */}
      <p style={cardStyles.disclaimer}>
        ℹ️ {meets 
          ? 'AI estimate — final decision by your PM during planning.' 
          : 'AI estimate — you can still submit and your PM will review during planning.'}
      </p>
    </div>
  );
};

// Inline styles for the card (avoids needing a separate CSS file)
const cardStyles: Record<string, React.CSSProperties> = {
  container: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '16px 20px',
    margin: '12px 0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    animation: 'chatbot-fadeIn 0.4s ease-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  headerIcon: {
    fontSize: '16px',
  },
  headerTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#4a5568',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#a0aec0',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  rationale: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: 1.6,
    margin: '0 0 12px 0',
  },
  disclaimer: {
    fontSize: '12px',
    color: '#718096',
    margin: 0,
    fontStyle: 'italic' as const,
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '12px',
    padding: '16px 0',
  },
  shimmer: {
    width: '200px',
    height: '8px',
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #edf2f7 25%, #e2e8f0 50%, #edf2f7 75%)',
    backgroundSize: '200% 100%',
    animation: 'priorityShimmer 1.5s infinite',
  },
  loadingText: {
    fontSize: '13px',
    color: '#718096',
    margin: 0,
  },
};

const shimmerAnimation = `
  @keyframes priorityShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export default PrioritySignalCard;
